/**
 * Where Standby lives.
 *
 * One place, because the domain is about to change and it is currently spelled
 * out in nine locale files, the OG tags, the developer docs and the Discord
 * bot. A move should be an edit here plus two environment variables, not a
 * search-and-replace across the repo that misses one.
 *
 * The API base is deliberately NOT here — it comes from the build environment
 * (REACT_APP_BACKEND_URL_PROD), so the API host can be cut over independently
 * of the site host and rolled back without a deploy.
 */

/**
 * The canonical public origin — every link, redirect and absolute URL.
 *
 * It carries the www because the apex cannot reach us: standbyq.app is
 * registered at Squarespace, whose DNS offers no ALIAS or ANAME record, and
 * Heroku is only addressable by CNAME (its IPs move, so A records are not an
 * option). www can be a CNAME; the apex cannot. Squarespace forwards the apex
 * here instead, which works because it already terminates TLS on the apex —
 * necessary, since .app is HSTS-preloaded and the redirect has to happen over
 * HTTPS or not at all.
 */
export const SITE_ORIGIN = 'https://www.standbyq.app';

/**
 * How the domain is written for people to read, and deliberately the bare
 * form. It is what the promo film says and what anyone would type, and typing
 * it works — the apex forwards. Prose gets the memorable name; anything a
 * machine follows gets SITE_ORIGIN, which skips the extra hop.
 */
export const SITE_DOMAIN = 'standbyq.app';

/**
 * Origins Standby used to answer on, kept working rather than switched off.
 *
 * They still matter after the move for longer than you would expect: push
 * subscriptions belong to the service worker registration on the origin that
 * created them, so a phone that enabled notifications on the old host keeps
 * receiving them there, and the notification opens an old-host URL. Anyone
 * who bookmarked, installed the PWA, or was sent an invite email before the
 * cutover also arrives here. All of it must land on the new site rather than
 * on nothing.
 */
export const LEGACY_ORIGINS = ['https://standby.squarebitstudios.com'];

/** Absolute URL on the canonical origin. `path` should start with a slash. */
export const siteUrl = (path = '/'): string => `${SITE_ORIGIN}${path}`;

/**
 * The API origin this build actually talks to, for anywhere we show it to
 * developers. Read from the build environment rather than written down, so the
 * docs cannot drift from the host the app is really calling — including during
 * the window where the two live on different domains.
 */
export const API_ORIGIN =
  process.env.REACT_APP_BACKEND_URL || 'https://api.standby.squarebitstudios.com';
